// subpath import: the map must not pull the whole engine into the first load
import { bossUnlocked, unlocks, type Unlocks } from "@mathbee/engine/unlock";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useChildState, useCurriculum } from "../api/hooks.ts";
import type { BookIndex, ChildState, LessonIndex, TopicIndex } from "../api/types.ts";
import { Bee } from "../art/Bee.tsx";
import { Island } from "../art/Island.tsx";
import { DailyCard, QuickTiles, ReviewCard } from "../components/PracticeCards.tsx";
import { PrizeCard } from "../components/PrizeCard.tsx";
import { QuestCard } from "../components/QuestCard.tsx";
import { Bubble, Button, Icon, Spinner, Stars } from "../components/ui.tsx";
import { copy } from "../copy/index.ts";
import { ErrorScreen } from "./ErrorScreen.tsx";
import styles from "./Home.module.css";

const ROW = 200;
const TOP = 50; // room for the first island badge

/** C4 – journey map (SPEC §6.1, §8.2). */
export function HomeScreen() {
  const state = useChildState();
  const curriculum = useCurriculum(state.data?.child.curriculumId);
  const [volume, setVolume] = useState<number | null>(null);
  const [openTopic, setOpenTopic] = useState<TopicIndex | null>(null);
  const navigate = useNavigate();

  const suggestion = useMemo(
    () => (curriculum.data && state.data ? suggestLesson(curriculum.data.books, state.data) : null),
    [curriculum.data, state.data],
  );
  // SPEC 6.2: lessons open one after another unless the parent turned on free unlock
  const freeUnlock = state.data?.child.freeUnlock ?? true;
  const open = useMemo(
    () =>
      curriculum.data && state.data
        ? unlocks(curriculum.data.books, state.data.progress, freeUnlock)
        : null,
    [curriculum.data, state.data, freeUnlock],
  );

  if (curriculum.isPending || state.isPending) return <Spinner />;
  if (curriculum.isError || !curriculum.data || !state.data) {
    return <ErrorScreen onRetry={() => void curriculum.refetch()} />;
  }
  const books = curriculum.data.books;
  const activeVolume = volume ?? suggestion?.book.volume ?? books[0]?.volume ?? 1;
  const book = books.find((b) => b.volume === activeVolume) ?? books[0];
  if (!book || !open) return null;
  const progress = state.data.progress;
  const goDaily = () => void navigate("/luyen-tap/hang-ngay");
  const goReview = () => void navigate("/luyen-tap/on-tap");
  const goLearn = () => suggestion && void navigate(`/bai/${suggestion.lesson.id}`);

  return (
    <div className={styles.home}>
      <section className={styles.hello}>
        <Bee expression="wave" size={96} />
        <Bubble>
          <strong className={styles.helloName}>
            {copy.home.greeting(state.data.child.displayName)}
          </strong>
          <br />
          {copy.home.greetingSub}
        </Bubble>
      </section>

      <PrizeCard prizes={state.data.leaderboardPrizes ?? []} />

      <div className={styles.layout}>
        <section className={styles.mapCol}>
          <div className={styles.tabs} role="tablist">
            {books.map((b) => (
              <button
                key={b.id}
                type="button"
                role="tab"
                aria-selected={b.volume === activeVolume}
                className={`${styles.tab} ${b.volume === activeVolume ? styles.tabOn : ""}`}
                onClick={() => setVolume(b.volume)}
              >
                {copy.home.volume(b.volume)}
              </button>
            ))}
          </div>
          <JourneyMap
            book={book}
            progress={progress}
            islands={state.data.islands ?? {}}
            open={open}
            onOpen={setOpenTopic}
          />
        </section>

        <aside className={styles.sideCol}>
          {suggestion && (
            <div className={styles.continue}>
              <h2>{copy.home.continueTitle}</h2>
              <p className={styles.continueTopic}>
                {copy.home.lessonsOf(suggestion.topic.number)} · {suggestion.topic.island?.name}
              </p>
              <p className={styles.continueLesson}>
                {copy.lesson.number(suggestion.lesson.number)}. {suggestion.lesson.title}
              </p>
              <Stars value={progress[suggestion.lesson.id]?.bestStars ?? 0} />
              <Button block onClick={() => void navigate(`/bai/${suggestion.lesson.id}`)}>
                {copy.home.continueCta} ›
              </Button>
            </div>
          )}
          <QuestCard />
          <div className={styles.narrowOnly}>
            <QuickTiles
              state={state.data}
              onDaily={goDaily}
              onReview={goReview}
              onLearn={goLearn}
            />
          </div>
          <div className={styles.wideOnly}>
            <DailyCard state={state.data} onStart={goDaily} onLearn={goLearn} />
            <ReviewCard state={state.data} onStart={goReview} />
          </div>
        </aside>
      </div>

      {openTopic && (
        <TopicSheet
          topic={openTopic}
          progress={progress}
          island={state.data.islands?.[openTopic.id]}
          open={open}
          freeUnlock={freeUnlock}
          onClose={() => setOpenTopic(null)}
          onLesson={(l) => void navigate(`/bai/${l.id}`)}
          onBoss={() => void navigate(`/dao/${openTopic.id}/thu-thach`)}
        />
      )}
    </div>
  );
}

function topicStars(topic: TopicIndex, progress: ChildState["progress"]) {
  const playable = topic.lessons.filter((l) => l.playable);
  return {
    playable: playable.length,
    got: playable.reduce((s, l) => s + (progress[l.id]?.bestStars ?? 0), 0),
    max: playable.length * 3,
  };
}

function suggestLesson(books: BookIndex[], state: ChildState) {
  const all = books.flatMap((book) =>
    book.topics.flatMap((topic) => topic.lessons.map((lesson) => ({ book, topic, lesson }))),
  );
  const playable = all.filter((x) => x.lesson.playable);
  return (
    playable.find((x) => (state.progress[x.lesson.id]?.bestStars ?? 0) < 3) ?? playable[0] ?? null
  );
}

function JourneyMap({
  book,
  progress,
  islands,
  open,
  onOpen,
}: {
  book: BookIndex;
  progress: ChildState["progress"];
  islands: ChildState["islands"];
  open: Unlocks;
  onOpen: (t: TopicIndex) => void;
}) {
  const n = book.topics.length;
  const points = book.topics.map((_, i) => ({
    x: i % 2 === 0 ? 30 : 70,
    y: TOP + i * ROW + ROW / 2,
  }));
  const d = points
    .map((p, i) => {
      if (i === 0) return `M${p.x} ${p.y}`;
      const prev = points[i - 1] as { x: number; y: number };
      return `C${prev.x} ${prev.y + ROW * 0.55} ${p.x} ${p.y - ROW * 0.55} ${p.x} ${p.y}`;
    })
    .join(" ");

  return (
    <div className={styles.ocean} style={{ height: TOP + n * ROW + 20 }}>
      <svg
        className={styles.path}
        viewBox={`0 0 100 ${TOP + n * ROW + 20}`}
        preserveAspectRatio="none"
        aria-hidden="true"
      >
        <path
          d={d}
          fill="none"
          stroke="#ffffff"
          strokeWidth="3"
          strokeDasharray="2 10"
          strokeLinecap="round"
          vectorEffect="non-scaling-stroke"
        />
      </svg>
      <div className={styles.cloud} style={{ top: 40 }} aria-hidden="true" />
      <div
        className={styles.cloud}
        style={{ top: ROW * 2.4, animationDelay: "-12s" }}
        aria-hidden="true"
      />
      {book.topics.map((topic, i) => {
        const s = topicStars(topic, progress);
        const soon = s.playable === 0;
        // playable but the previous island is not finished yet (SPEC 6.2)
        const closed = !soon && !open.islands.has(topic.id);
        const locked = soon || closed;
        const trophy = islands[topic.id]?.trophy ?? false;
        const p = points[i] as { x: number; y: number };
        return (
          <button
            key={topic.id}
            type="button"
            className={`${styles.island} ${locked ? styles.locked : ""}`}
            style={{ left: `${p.x}%`, top: p.y }}
            onClick={() => onOpen(topic)}
            aria-label={`Chủ đề ${topic.number}: ${topic.title}${soon ? `, ${copy.home.comingSoonIsland}` : closed ? `, ${copy.home.islandLocked}` : ""}${trophy ? `, ${copy.home.boss.trophyLabel}` : ""}`}
          >
            <span className={styles.badge}>{topic.number}</span>
            <Island biome={topic.island?.biome ?? "meadow"} locked={locked} size={190} />
            <span className={styles.label}>
              <strong>{topic.island?.name ?? topic.title}</strong>
              <span className={styles.labelSub}>{topic.title}</span>
              {locked ? (
                <span className={styles.soon}>
                  <Icon name="lock" size={16} />{" "}
                  {soon ? copy.home.comingSoonIsland : copy.home.islandLocked}
                </span>
              ) : (
                <span className={styles.islandStars}>
                  <Icon name="star" size={18} /> {copy.home.islandStars(s.got, s.max)}
                  {trophy && <Icon name="trophy" size={22} />}
                </span>
              )}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function TopicSheet({
  topic,
  progress,
  island,
  open,
  freeUnlock,
  onClose,
  onLesson,
  onBoss,
}: {
  topic: TopicIndex;
  progress: ChildState["progress"];
  island: ChildState["islands"][string] | undefined;
  open: Unlocks;
  freeUnlock: boolean;
  onClose: () => void;
  onLesson: (l: LessonIndex) => void;
  onBoss: () => void;
}) {
  const [lockedTap, setLockedTap] = useState(false);
  const playableIds = topic.lessons.filter((l) => l.playable).map((l) => l.id);
  const bossOpen = bossUnlocked(
    playableIds,
    Object.fromEntries(playableIds.map((id) => [id, progress[id]?.bestStars ?? 0])),
    freeUnlock,
  );
  return (
    <div className={styles.backdrop} onClick={onClose} role="presentation">
      <div
        className={styles.sheet}
        role="dialog"
        aria-modal="true"
        aria-label={topic.title}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.sheetHead}>
          <Island biome={topic.island?.biome ?? "meadow"} size={120} />
          <div>
            <p className={styles.sheetKicker}>{copy.home.lessonsOf(topic.number)}</p>
            <h2>{topic.island?.name}</h2>
            <p className={styles.sheetTitle}>{topic.title}</p>
          </div>
          <button type="button" className={styles.close} onClick={onClose} aria-label="Đóng">
            <Icon name="close" size={22} />
          </button>
        </div>
        <ol className={styles.lessons}>
          {topic.lessons.map((l) => {
            const locked = l.playable && !open.lessons.has(l.id);
            return (
              <li key={l.id}>
                <button
                  type="button"
                  className={`${styles.lessonRow} ${l.playable && !locked ? "" : styles.lessonSoon}`}
                  aria-label={locked ? `${l.title}, ${copy.home.lessonLockedLabel}` : undefined}
                  onClick={() => (locked ? setLockedTap(true) : onLesson(l))}
                >
                  <span className={styles.lessonNum}>{l.number}</span>
                  <span className={styles.lessonTitle}>{l.title}</span>
                  {l.playable && (progress[l.id]?.challengeBestStars ?? 0) >= 2 && (
                    <span className={styles.brain} title={copy.lesson.brainMark}>
                      <Icon name="brain" size={24} />
                    </span>
                  )}
                  {locked ? (
                    <Icon name="lock" size={22} />
                  ) : l.playable ? (
                    <Stars value={progress[l.id]?.bestStars ?? 0} size={22} />
                  ) : (
                    <span className={styles.soonTag}>{copy.lesson.comingSoon}</span>
                  )}
                </button>
              </li>
            );
          })}
        </ol>
        {lockedTap && (
          <p className={styles.lockedHint} role="status">
            <Icon name="lock" size={18} /> {copy.home.lessonLocked}
          </p>
        )}
        {playableIds.length > 0 && (
          <button
            type="button"
            className={`${styles.bossRow} ${bossOpen ? "" : styles.bossLocked}`}
            onClick={onBoss}
            disabled={!bossOpen}
          >
            <Icon name="trophy" size={48} />
            <span className={styles.bossText}>
              <strong>{copy.home.boss.title}</strong>
              <span>
                {island?.trophy
                  ? copy.home.boss.won
                  : bossOpen
                    ? copy.home.boss.open
                    : copy.home.boss.locked}
              </span>
            </span>
            {bossOpen ? (
              <Stars value={island?.bossBestStars ?? 0} size={22} />
            ) : (
              <Icon name="lock" size={22} />
            )}
          </button>
        )}
      </div>
    </div>
  );
}
